import { useState, useEffect, useRef, useMemo } from 'react';
import {
  getReferencesForBrand,
  getReferencesForBrandPaginated,
  getReferencesCountForBrand,
  getImageUrl,
  createWatchReference,
  normalizeReference,
  getWatchReference,
  computeSearchText,
  deleteAllReferencesForBrand,
  getBrandImportSources,
  getElectronicModuleById,
} from '../../services/firebase';
import type { ReferenceSort } from '../../services/firebase';
import {
  normalizeCalibreKey,
  resolveDisplayCalibre,
} from '../../services/calibreResolution';
import {
  mergeRefreshedHitPages,
  referenceHasNoCalibre,
  referenceHasNoImage,
  referenceNeedsAiEnrichment,
} from '../../services/referenceListQuery';
import { DescriptionSourceBadge } from './DescriptionSourceBadge';
import { useRelevanceSort } from '../../hooks/useRelevanceSort';
import { useResolvedHitImage } from '../../hooks/useResolvedHitImage';
import { RELEVANCE_SORT, type WithRelevance } from '../../services/relevanceSort';
import type { ElectronicModule } from '../../types';
import { ElectronicModuleEditorModal } from '../ElectronicModules';
import {
  searchReferences,
  getBrandListingFacets,
  isAlgoliaConfigured,
} from '../../services/algolia';
import type {
  BrandFacetValue,
  ReferenceSortKey,
} from '../../services/algolia';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { BrandLogo } from '../common/BrandLogo';
import { Modal } from '../common/Modal';
import { BulkImport } from './BulkImport';
import { RetryMissingImages } from './RetryMissingImages';
import { BackfillProductUrls } from './BackfillProductUrls';
import { ProbeShopify } from './ProbeShopify';
import { EnrichmentTasksPanel } from './EnrichmentTasksPanel';
import { ReferenceCard } from './ReferenceCard';
import { ReferenceBulkActionBar, applyDotPathUpdates } from './ReferenceBulkActionBar';
import { RunScraperModal } from '../Extractors';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { canRunScraper } from '../../services/extractorMatching';
import { autoRunDecision } from '../../services/extractorAutoRun';
import {
  fetchBrandOverrides,
  fetchScrapers,
  type ScraperDescriptor,
} from '../../services/extractorService';
import type { WatchBrand, WatchReference, WatchReferenceAlgoliaHit } from '../../types';
import { type QueryDocumentSnapshot, type DocumentData } from 'firebase/firestore';
import toast from 'react-hot-toast';

// ── StatPill ─────────────────────────────────────────────────────────────────

function StatPill({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: 'emerald' | 'blue' | 'amber';
}) {
  const cls =
    accent === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : accent === 'blue'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : accent === 'amber'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${cls}`}>
      <span className="font-semibold tabular-nums">{value}</span>
      <span className="text-opacity-75">{label}</span>
    </div>
  );
}

// ── Hit rendering ─────────────────────────────────────────────────────────────

interface SearchResultCardProps {
  hit: WatchReferenceAlgoliaHit;
  onClick: () => void;
  loading?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /** Corner badge text. Omit for the plain browse listing. */
  badge?: string;
}

function SearchResultCard({ hit, onClick, loading, selected, onSelect, badge }: SearchResultCardProps) {
  const displayUrl = useResolvedHitImage(hit.imageUrl);

  return (
    <div
      className={`bg-white shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${loading ? 'opacity-50 pointer-events-none' : ''} ${selected ? 'ring-2 ring-brand-500' : ''}`}
      onClick={onClick}
    >
      <div className="aspect-square bg-gray-100 relative">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={hit.reference}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            {loading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            ) : (
              <svg
                className="w-12 h-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            )}
          </div>
        )}
        {loading && displayUrl && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        )}
        {/* Checkbox */}
        {onSelect !== undefined && (
          <div
            className="absolute top-2 left-2"
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
          >
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => {}}
              className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
            />
          </div>
        )}
        {badge && (
          <Badge variant="info" className="absolute top-2 right-2">
            {badge}
          </Badge>
        )}
      </div>

      <div className="p-3">
        <h3 className="font-medium text-gray-900 truncate">
          {hit.reference}
        </h3>
        <p className="text-sm text-gray-600 truncate">
          {hit.model || hit.productName || '-'}
        </p>
        {hit.collection && (
          <p className="text-xs text-gray-500 truncate">
            {hit.collection}
          </p>
        )}
      </div>
    </div>
  );
}

// ── HitRow (list view, Algolia-backed listing) ────────────────────────────────

interface HitRowProps {
  hit: WatchReferenceAlgoliaHit;
  onClick: () => void;
  loading: boolean;
  selected: boolean;
  onSelect: () => void;
}

/**
 * List-view row rendered straight from an Algolia hit.
 *
 * Column-for-column with {@link ReferenceRow}, including Module now that
 * watchlock #860 indexes `moduleId` + `moduleNumber`. The only difference is
 * that the Module cell here is read-only: the per-row AI-enrich affordance needs
 * the linked module's full document, which a search hit isn't.
 */
function HitRow({ hit, onClick, loading, selected, onSelect }: HitRowProps) {
  const imageUrl = useResolvedHitImage(hit.imageUrl);

  return (
    <tr
      className={`hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${selected ? 'bg-blue-50' : ''} ${loading ? 'opacity-50' : ''}`}
      onClick={onClick}
    >
      <td className="px-3 py-2 w-8" onClick={(e) => { e.stopPropagation(); onSelect(); }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => {}}
          className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
        />
      </td>
      <td className="px-3 py-2 w-14">
        {imageUrl ? (
          <img src={imageUrl} alt={hit.reference} className="w-10 h-10 object-cover rounded" />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </td>
      <td className="px-3 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
        {hit.reference}
      </td>
      <td className="px-3 py-2 text-sm text-gray-600 max-w-[160px] truncate">
        {hit.model || hit.productName || '-'}
      </td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{hit.collection || '-'}</td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{hit.calibre || '-'}</td>
      {/* Module. `moduleId` is the link, `moduleNumber` the human label off the
          ref's electronics cache; the index omits both when unset, so absent
          means "no module". Read-only here — the enrich affordance in the
          fallback row needs the full document, which a hit isn't. */}
      <td className="px-3 py-2 text-sm whitespace-nowrap">
        {hit.moduleId ? (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {hit.moduleNumber || 'linked'}
          </span>
        ) : hit.moduleNumber ? (
          <span className="text-gray-400" title="Inline value — no module linked">
            {hit.moduleNumber}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      {/* Description provenance. A hit only says what the indexer projects, and
          it does not project `descriptionSource` yet — so a hit without it is
          "unknown", not "legacy". Calling it legacy would paint every row amber. */}
      <td className="px-3 py-2 text-sm whitespace-nowrap">
        <DescriptionSourceBadge source={hit.descriptionSource} absentMeans="unknown" />
      </td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">
        {hit.caseSizeMm ? `${hit.caseSizeMm}mm` : '-'}
      </td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{hit.movementType || '-'}</td>
    </tr>
  );
}

// ── ReferenceRow (list view) ──────────────────────────────────────────────────

interface ReferenceRowProps {
  reference: WatchReference;
  onClick: () => void;
  selected: boolean;
  onSelect: () => void;
  /** Open the AI enrich flow for this ref's linked module. */
  onEnrichModule: () => void;
}

function ReferenceRow({ reference, onClick, selected, onSelect, onEnrichModule }: ReferenceRowProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (reference.images && reference.images[0]?.storagePath) {
      getImageUrl(reference.images[0].storagePath)
        .then(url => setImageUrl(url))
        .catch(() => setImageUrl(null));
    }
  }, [reference.images]);

  const caseSize =
    reference.case?.diameter ??
    reference.case?.sizeMm ??
    reference.case?.diameterMm;

  return (
    <tr
      className={`hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${selected ? 'bg-blue-50' : ''}`}
      onClick={onClick}
    >
      <td className="px-3 py-2 w-8" onClick={(e) => { e.stopPropagation(); onSelect(); }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => {}}
          className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
        />
      </td>
      <td className="px-3 py-2 w-14">
        {imageUrl ? (
          <img src={imageUrl} alt={reference.reference} className="w-10 h-10 object-cover rounded" />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </td>
      <td className="px-3 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
        {reference.reference}
      </td>
      <td className="px-3 py-2 text-sm text-gray-600 max-w-[160px] truncate">{reference.model || '-'}</td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{reference.collection || '-'}</td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">
        {resolveDisplayCalibre(reference) || '-'}
      </td>
      <td className="px-3 py-2 text-sm whitespace-nowrap">
        {(() => {
          const linked = !!reference.moduleId;
          const moduleName = reference.electronics?.module;
          return (
            <div className="flex items-center gap-2">
              {linked ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {moduleName || 'linked'}
                </span>
              ) : moduleName ? (
                <span className="text-gray-400" title="Inline value — no module linked">
                  {moduleName}
                </span>
              ) : (
                <span className="text-gray-300">-</span>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEnrichModule(); }}
                disabled={!linked}
                title={linked ? 'AI-enrich the linked module' : 'No module linked'}
                className="text-xs px-1.5 py-0.5 rounded border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Enrich
              </button>
            </div>
          );
        })()}
      </td>
      {/* Description provenance. This row is a Firestore document, so a missing
          `descriptionSource` really is a legacy ref. */}
      <td className="px-3 py-2 text-sm whitespace-nowrap">
        <DescriptionSourceBadge
          source={reference.descriptionSource}
          absentMeans="legacy"
          rewrittenAt={reference.descriptionAiRewrittenAt}
        />
      </td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">
        {caseSize ? `${caseSize}mm` : '-'}
      </td>
      <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap">{reference.movementType || '-'}</td>
    </tr>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────

const COUNTRY_FLAG: Record<string, string> = {
  switzerland: '🇨🇭', japan: '🇯🇵', germany: '🇩🇪', france: '🇫🇷',
  'united states': '🇺🇸', usa: '🇺🇸', 'united kingdom': '🇬🇧', uk: '🇬🇧',
  italy: '🇮🇹', denmark: '🇩🇰', sweden: '🇸🇪', finland: '🇫🇮',
  norway: '🇳🇴', russia: '🇷🇺', china: '🇨🇳', canada: '🇨🇦',
  australia: '🇦🇺', 'hong kong': '🇭🇰', singapore: '🇸🇬', spain: '🇪🇸',
  netherlands: '🇳🇱', austria: '🇦🇹', 'czech republic': '🇨🇿',
  poland: '🇵🇱', portugal: '🇵🇹', 'south korea': '🇰🇷', taiwan: '🇹🇼',
  india: '🇮🇳', israel: '🇮🇱', belgium: '🇧🇪',
};

/**
 * Creation time of a ref in epoch ms, or null when it has none. Typed as a
 * Firestore `Timestamp` but tolerant of the `Date`/number shapes older docs and
 * cached writes can carry.
 */
function refCreatedAtMs(ref: WatchReference): number | null {
  const raw = ref.createdAt as unknown;
  if (!raw) return null;
  if (typeof raw === 'number') return raw;
  if (raw instanceof Date) return raw.getTime();
  if (typeof (raw as { toMillis?: unknown }).toMillis === 'function') {
    return (raw as { toMillis: () => number }).toMillis();
  }
  return null;
}

// ── ReferenceBrowser ──────────────────────────────────────────────────────────

interface ReferenceBrowserProps {
  brand: WatchBrand;
  onBack: () => void;
  onSelectReference: (reference: WatchReference) => void;
  onEditBrand?: () => void;
  onBrandUpdated?: (updatedBrand: WatchBrand) => void;
  updatedReference?: WatchReference | null;
  deletedReferenceId?: string | null;
  /**
   * Navigate up to the dedicated bulk-import screen (Import Sources) for this
   * brand — its one entry point from the brand, now the editor's own button is
   * gone. When provided, the "Bulk Import" button routes there instead of opening the
   * legacy inline modal (which remains as the fallback when this is absent).
   */
  onOpenBulkImport?: (brandId: string) => void;
  /**
   * Opens Catalogue → Extractor Jobs, for the link out of the Run scraper
   * modal's status card. Absent means no link is offered — the run still starts
   * and still reports here.
   */
  onOpenExtractorJobs?: (brandId: string) => void;
  /**
   * Open the Run scraper modal once this listing has settled — the extractor
   * config screen's Open action, which lands here precisely because this is
   * where the Run button lives.
   *
   * A REQUEST, not a command, and deliberately not a click on the button: the
   * button does not exist yet when this arrives. Three loads stand between the
   * request and a runnable screen — the brand document, this component's mount,
   * and the scraper catalogue plus the override map — so anything that reached
   * for the DOM would be racing all three.
   */
  autoRunScraper?: boolean;
  /** Called once the request has been acted on, or declined. Always called. */
  onAutoRunScraperConsumed?: () => void;
}

const PAGE_SIZE = 50;

/**
 * Hits per page for the Algolia-backed listing. Larger than the Firestore page
 * because these are projected records (twelve attributes), not full documents —
 * and because the calibre filter once showed EVERY match at once (it filtered a
 * full in-memory brand fetch), so a small page would be a regression. Anything
 * beyond this pages in via Load More.
 */
const LISTING_PAGE_SIZE = 200;

/**
 * Cap on the one-shot, parents-only fetch of a whole brand — the set both the
 * Firestore fallback's dropdowns and the "without calibre" filter work over.
 *
 * A brand with more parents than this is truncated, and the truncation is
 * surfaced rather than swallowed: a filter that says "42 refs have no calibre"
 * off a capped set is reporting 42 of an unknown number.
 */
const FULL_BRAND_SET_LIMIT = 10000;

/**
 * Idle time before a keystroke becomes a search. Long enough that typing a
 * reference number is one request rather than one per character, short enough
 * that the list feels live.
 */
const SEARCH_DEBOUNCE_MS = 300;

/** Display labels for the indexed `movementType` enum. */
const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  automatic: 'Automatic',
  manual: 'Manual',
  quartz: 'Quartz',
  'solar-quartz': 'Solar quartz',
  'kinetic-quartz': 'Kinetic quartz',
  'spring-drive': 'Spring Drive',
  mechanical: 'Mechanical',
  digital: 'Digital',
  hybrid: 'Hybrid',
};

/**
 * Algolia's default `paginationLimitedTo`. `nbHits` reports the true match
 * count, but records past this offset are NOT retrievable — paging beyond it
 * errors rather than returning more. The listing caps Load More here and says so
 * instead of quietly stopping.
 */
const ALGOLIA_MAX_RETRIEVABLE = 1000;

const SORT_LABELS = {
  reference: 'Reference',
  model: 'Model',
  collection: 'Collection',
  createdAt: 'Created',
  relevance: 'Relevance',
} as const;

/** The sort the listing opens on, and the one relevance hands back to. */
const DEFAULT_REFERENCE_SORT = 'reference' as const;

export function ReferenceBrowser({
  brand,
  onBack,
  onSelectReference,
  onEditBrand,
  onBrandUpdated,
  updatedReference,
  deletedReferenceId,
  onOpenBulkImport,
  onOpenExtractorJobs,
  autoRunScraper,
  onAutoRunScraperConsumed,
}: ReferenceBrowserProps) {
  const [references, setReferences] = useState<WatchReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Canonical Shopify status for this brand comes from its import-sources doc
  // (a source of kind 'shopify'), not the legacy shopifyEnabled/shopifyUrl
  // fields which can drift when a source is removed. Seed from the legacy field
  // so the pill doesn't flicker, then reconcile against the sources doc.
  const [hasShopifySource, setHasShopifySource] = useState(
    Boolean(brand.shopifyEnabled || brand.shopifyUrl),
  );
  useEffect(() => {
    let cancelled = false;
    setHasShopifySource(Boolean(brand.shopifyEnabled || brand.shopifyUrl));
    (async () => {
      try {
        const config = await getBrandImportSources(brand.id);
        if (cancelled || !config) return;
        setHasShopifySource(config.sources.some((s) => s.kind === 'shopify'));
      } catch (error) {
        console.error('Failed to load brand import sources:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brand.id, brand.shopifyEnabled, brand.shopifyUrl]);

  /*
   * Run scraper — a top-level action for this brand, alongside Bulk Import.
   *
   * It used to live inside the brand EDIT modal, which is a form for brand
   * metadata: an operator who lands on this page after clicking a brand had to
   * open Edit and close it again to start a crawl, and had no reason to look
   * there for one. The gate is unchanged (feature flag AND a registered module
   * that resolves to this brand id, overrides included); only the placement is.
   *
   * The catalogue is fetched once per session and cached with its ETag by
   * `fetchScrapers`, so browsing several brands costs one call. A failure is
   * swallowed: an unreachable extractor means no button, the same outcome as
   * "no module covers this brand" — Test connection on the config screen is
   * where an operator gets a real diagnosis.
   */
  const extractorJobsEnabled = useFeatureFlag('tools.extractorJobs');
  const [scraperCatalogue, setScraperCatalogue] = useState<ScraperDescriptor[] | null>(null);
  const [scraperBrandOverrides, setScraperBrandOverrides] = useState<Record<string, string>>({});
  /**
   * Whether the override map has actually been read.
   *
   * `{}` is the initial value AND a legitimate result, so the map alone cannot
   * tell "no overrides exist" from "not fetched yet". The button never cared —
   * it just re-renders — but the auto-run gate below decides ONCE, and deciding
   * against an unread map would refuse exactly the brands that need an override
   * to resolve.
   */
  const [scraperOverridesLoaded, setScraperOverridesLoaded] = useState(false);
  const [showRunScraper, setShowRunScraper] = useState(false);

  useEffect(() => {
    if (!extractorJobsEnabled || scraperCatalogue !== null) return;
    let cancelled = false;
    void fetchBrandOverrides()
      .then((map) => {
        if (!cancelled) setScraperBrandOverrides(map);
      })
      .catch(() => {
        /* No overrides is the same outcome as an unreachable config doc. */
      })
      .finally(() => {
        if (!cancelled) setScraperOverridesLoaded(true);
      });
    void fetchScrapers()
      .then((list) => {
        if (!cancelled) setScraperCatalogue(list);
      })
      .catch(() => {
        if (!cancelled) setScraperCatalogue([]);
      });
    return () => {
      cancelled = true;
    };
  }, [extractorJobsEnabled, scraperCatalogue]);

  /*
   * Act on an auto-run request from the extractor config screen's Open action.
   *
   * Waits on the STATE that gates the button, never on the button itself. By the
   * time this component mounts, the request has already survived the brand-doc
   * fetch upstream; here it still has to wait for the scraper catalogue AND the
   * override map, which resolve independently. Evaluating on the catalogue alone
   * would run `canRunScraper` against an unread override map and decline the
   * brands overrides exist for.
   *
   * Opens the modal by the same state the button sets, rather than dispatching a
   * click: the button may legitimately never render, and a synthetic click would
   * be racing three loads for the privilege of finding out.
   *
   * Consumed exactly once, and consumed even when the answer is no — the flag
   * lives on the dashboard, so a request left armed would fire on whatever brand
   * the operator opened next. When the gate says no the reason is always the
   * same two: the feature flag is off (it does not gate the config screen the
   * request came from), or no module resolves to this brand.
   */
  const autoRunConsumed = useRef(false);
  useEffect(() => {
    if (autoRunConsumed.current) return;
    const decision = autoRunDecision({
      requested: autoRunScraper === true,
      flagEnabled: extractorJobsEnabled,
      catalogueLoaded: scraperCatalogue !== null,
      overridesLoaded: scraperOverridesLoaded,
      canRun: canRunScraper(
        extractorJobsEnabled,
        scraperCatalogue,
        brand,
        scraperBrandOverrides,
      ),
    });
    // `wait` is not a decision — come back on the next render, request intact.
    if (decision === 'idle' || decision === 'wait') return;

    autoRunConsumed.current = true;
    if (decision === 'run') {
      setShowRunScraper(true);
    } else if (decision === 'decline-disabled') {
      toast.error('Run scraper is turned off — enable the extractor jobs feature flag.');
    } else {
      toast.error(`No extractor module resolves to ${brand.name || brand.id}.`);
    }
    onAutoRunScraperConsumed?.();
  }, [
    autoRunScraper,
    extractorJobsEnabled,
    scraperCatalogue,
    scraperOverridesLoaded,
    scraperBrandOverrides,
    brand,
    onAutoRunScraperConsumed,
  ]);

  /* A different brand is a different request. */
  useEffect(() => {
    autoRunConsumed.current = false;
  }, [brand.id]);

  // Search box. `activeQuery` is the debounced term the listing actually runs —
  // typing is live (Algolia's typo tolerance is only useful if you can see it
  // work), and the Search button just flushes the debounce.
  const [searchTerm, setSearchTerm] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () => setActiveQuery(searchTerm.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchTerm]);
  const [loadingReferenceId, setLoadingReferenceId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Pagination. `totalCount` is every doc in the subcollection (parents,
  // standalones AND Shopify variants), fetched by its own aggregation query
  // because the browse query's own count is parents-only. The counts users see
  // in the listing are `parentCount` (below); `totalCount` exists solely for
  // the delete-all flow, which really does wipe everything.
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  // Monotonic token guarding the filter-option load against stale overwrites.
  const filterOptionsReqId = useRef(0);

  // Parents + standalones for this brand — the population the listing shows.
  //
  // On the Algolia path this is `nbHits` for `brandId`, which is EXACT: the
  // index holds no variants at all (syncReferenceToAlgolia deletes any ref
  // carrying a `parentReferenceId`), so there is nothing to subtract.
  //
  // The Firestore fallback derives it from the fetched parent set. A
  // parents-only server COUNT is now expressible — `where('isVariant', '==',
  // false)` — but the fallback has to download those docs anyway to build the
  // filter dropdowns, so counting them costs nothing extra.
  const [parentCount, setParentCount] = useState(0);

  const algoliaConfigured = isAlgoliaConfigured();

  // The Algolia-backed listing. Browse (empty query), search (typed query) and
  // filter (facet filters) are ONE query against one page of state — they no
  // longer take three separate code paths, so they combine.
  const [hits, setHits] = useState<WatchReferenceAlgoliaHit[]>([]);
  const [hitsTotal, setHitsTotal] = useState(0);
  const [loadingHits, setLoadingHits] = useState(false);
  const [loadingMoreHits, setLoadingMoreHits] = useState(false);
  // The toolbar Refresh button's in-flight state.
  const [refreshing, setRefreshing] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const hitsPageRef = useRef(0);

  // Sort. `reference`/`model`/`collection` sort the already-loaded page
  // client-side; `createdAt` additionally drives the Firestore `orderBy` (see
  // browseSortKey) so refs from the newest import surface on page 1 rather than
  // wherever they happen to fall in reference order.
  type SortField = 'reference' | 'model' | 'collection' | 'createdAt';
  // Widened to admit `'relevance'`, which is not a field on a reference — see
  // `services/relevanceSort.ts`. Everything that indexes a row by the sort field
  // reads `effectiveSortField` instead.
  const [sortField, setSortField] = useState<WithRelevance<SortField>>(
    DEFAULT_REFERENCE_SORT,
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  /**
   * Relevance sort.
   *
   * This listing has ALWAYS returned relevance order for a typed query — it
   * queries the primary index with no sort replica, and `sortedHits` left those
   * hits alone. What it did not do was say so: the Reference button stayed lit
   * while the rows were ranked by match, and the whole button row was disabled,
   * so an admin who wanted their search sorted by Model could not have it.
   * Making relevance a sort field of its own is what fixes both halves.
   *
   * Gated on Algolia being configured: the Firestore fallback ranks nothing, so
   * there is no relevance to offer and `sortedReferences` below always sorts.
   */
  const {
    relevanceActive,
    effectiveSortField,
    relevanceAvailable,
    pickSort,
  } = useRelevanceSort<SortField>({
    query: algoliaConfigured ? activeQuery : '',
    sortField,
    defaultSortField: DEFAULT_REFERENCE_SORT,
    setSortField,
  });

  // Firestore `orderBy` for the paginated browse list. Only the createdAt sort
  // changes it — the other fields keep the historical reference-asc paging and
  // reorder the loaded page client-side. Stringified so the load effect re-runs
  // on a createdAt direction flip but not on an unrelated sort toggle.
  const browseSort: ReferenceSort | undefined =
    effectiveSortField === 'createdAt'
      ? { field: 'createdAt', direction: sortDir }
      : undefined;
  const browseSortKey = browseSort ? `${browseSort.field}:${browseSort.direction}` : 'default';

  // View mode
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Collection filter. '' = no filter.
  const [selectedCollection, setSelectedCollection] = useState('');

  // Calibre filter — one distinct calibre name per brand (cache first, legacy
  // string second — see resolveDisplayCalibre), deduped
  // case-insensitively. '' = no filter.
  const [selectedCalibre, setSelectedCalibre] = useState('');

  // Movement type filter — an indexed facet, so its options and counts arrive
  // free alongside the other two. '' = no filter.
  const [selectedMovementType, setSelectedMovementType] = useState('');

  /**
   * "Only refs without calibre" — the enrichment-gap filter.
   *
   * NOT an Algolia filter, and it cannot become one without a watchlock change.
   * The references index carries no `calibreId` attribute at all, and an empty
   * `calibre` is not a facet VALUE on it (verified against the live index: no
   * empty key among the facet values, and `facetFilters: ['calibre:']` is
   * silently dropped and returns everything). Algolia has no "attribute is
   * absent" operator either, so the only index-side answer is a `hasCalibre`
   * boolean written by the indexer — the shape `hasImage` already has — plus a
   * reindex.
   *
   * Until that exists this toggle drops the listing onto the FIRESTORE path and
   * filters the brand's full parent set in memory, which is the same machinery
   * the fallback path has always used for its own client-side filters. It costs
   * one brand-scoped read on tick, cached for the brand, and it is opt-in — the
   * default listing still spends nothing.
   */
  const [onlyMissingCalibre, setOnlyMissingCalibre] = useState(false);

  /**
   * "Only refs needing AI enrichment": descriptions that have not been
   * AI-rewritten or hand-written (see `referenceNeedsAiEnrichment`).
   *
   * Same constraint as the calibre gap above, for the same reason: the index
   * does not carry `descriptionSource`, and "absent" is not something a facet
   * can match. So this rides the identical Firestore path — one brand-scoped
   * read on first tick, shared with the calibre toggle if that already ran.
   */
  const [onlyNeedsAiEnrichment, setOnlyNeedsAiEnrichment] = useState(false);

  /**
   * Either enrichment-gap filter is on. Everything that is about WHERE the rows
   * come from — leaving the Algolia path, fetching and holding the full brand
   * set, what the stats count — keys on this, so the two toggles cannot drift
   * apart. Only the predicates themselves, the label and the checkboxes read
   * the individual flags.
   */
  const gapFilterActive = onlyMissingCalibre || onlyNeedsAiEnrichment;

  /**
   * "Only refs without image" — the picture-gap filter.
   *
   * Unlike the calibre toggle above, this one IS an index filter: `hasImage` is a
   * boolean facet written unconditionally by the indexer, so it runs server-side
   * like the three dropdowns and keeps the listing on Algolia. On the Firestore
   * fallback it filters in memory through `referenceHasNoImage`, which mirrors
   * the indexer's rule so both paths count the same refs.
   */
  const [onlyWithoutImage, setOnlyWithoutImage] = useState(false);

  /** References in this brand with no picture — the counter beside the toggle. */
  const [noImageCount, setNoImageCount] = useState<number | null>(null);

  // Where the LISTING, the filter dropdowns and `parentCount` come from.
  //   'algolia'   — one brand-scoped search per page, no Firestore reads.
  //                 Browse, search, filters AND the Created sort all run in the
  //                 index (parents only by construction, never capped by a
  //                 browse page). This is the listing.
  //   'firestore' — AUTOMATIC FALLBACK ONLY, never a choice: the historical
  //                 paginated fetch plus a one-shot full-brand fetch for the
  //                 dropdowns. Used when Algolia is unconfigured, when the brand
  //                 is DISABLED (the indexer evicts every ref of a disabled
  //                 brand, so the index would report an empty catalogue for
  //                 exactly the brand an admin needs to see), or when the index
  //                 comes back empty or errors.
  //
  // The admin-facing Source selector is gone. It existed because the index
  // carried no `moduleId` and no `createdAt` — watchlock #860 added both (plus a
  // `createdAt` sort replica and searchable `calibre`), so Firestore no longer
  // offers anything the index can't do and there is nothing left to choose.
  const [filterSource, setFilterSource] = useState<'algolia' | 'firestore'>(
    'algolia',
  );
  const [collectionOptions, setCollectionOptions] = useState<BrandFacetValue[]>([]);
  const [calibreOptions, setCalibreOptions] = useState<BrandFacetValue[]>([]);
  const [movementTypeOptions, setMovementTypeOptions] = useState<BrandFacetValue[]>([]);

  // The brand's parent/standalone refs, held in memory so a filter the index
  // can't express is applied across every ref rather than just the loaded browse
  // page. Populated by the FALLBACK path (where it also feeds the dropdowns) and,
  // lazily, by the "without calibre" toggle. Stays empty on the Algolia listing
  // with the toggle off — that is the read saving this whole panel exists for.
  const [fallbackBrandRefs, setFallbackBrandRefs] = useState<WatchReference[]>([]);
  // Which brand+generation `fallbackBrandRefs` holds, so the toggle re-uses a
  // set the fallback path already fetched rather than reading the brand twice —
  // and so a genuinely empty brand isn't re-read on every tick, which a length
  // check would do. The generation is what a bulk enrich bumps: refs it filled
  // in must drop out of a gap filter, not linger because the set is cached.
  const fullBrandSetFor = useRef<string | null>(null);
  // Mirrored in a ref so `loadFilterOptions` — an async function that can be
  // called moments after a bump — stamps the generation it is actually running
  // in rather than the one its closure captured.
  const fullBrandSetNonceRef = useRef(0);
  const [fullBrandSetNonce, setFullBrandSetNonce] = useState(0);
  const bumpFullBrandSet = () => {
    fullBrandSetNonceRef.current += 1;
    setFullBrandSetNonce(fullBrandSetNonceRef.current);
  };
  const [loadingFullBrandSet, setLoadingFullBrandSet] = useState(false);
  // True when the full-brand fetch came back at its cap, so the in-memory filter
  // is running over a truncated catalogue and must say so rather than quietly
  // under-reporting the gap.
  const [fullBrandSetTruncated, setFullBrandSetTruncated] = useState(false);

  // Per-row module enrichment: load the ref's linked module, then open its
  // editor (where the per-field AI enrich + apply-review lives).
  const [enrichModuleTarget, setEnrichModuleTarget] =
    useState<ElectronicModule | null>(null);

  const handleEnrichModule = async (ref: WatchReference) => {
    if (!ref.moduleId) return;
    try {
      const m = await getElectronicModuleById(ref.moduleId);
      if (!m) {
        toast.error('Linked module not found');
        return;
      }
      setEnrichModuleTarget(m);
    } catch (err) {
      console.error('Failed to load linked module:', err);
      toast.error('Failed to load linked module');
    }
  };

  // Multi-select. The bulk ACTIONS over this selection (and their modals) live in
  // ReferenceBulkActionBar; this component owns only the selection itself and
  // reconciles its list state through the bar's callbacks.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllBrand, setSelectAllBrand] = useState(false); // true = all refs for brand (not just visible)

  // Delete-all-for-brand (full wipe, for the composite-ID re-import migration)
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [deleteAllProgress, setDeleteAllProgress] = useState(0);
  const [deleteAllTotal, setDeleteAllTotal] = useState(0);

  // Select-all checkbox ref for indeterminate state
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Create form
  const [newReference, setNewReference] = useState('');
  const [newModel, setNewModel] = useState('');
  const [newCollection, setNewCollection] = useState('');
  const [newProductName, setNewProductName] = useState('');

  // ── Derived state ──────────────────────────────────────────────────────────

  // Includes the "without calibre" toggle: it narrows the view exactly as the
  // dropdowns do, so every consumer that has to know the view is narrowed — the
  // empty-state copy, and the brand-wide select-all banner, which must not offer
  // a selection that reaches far past what's on screen — has to count it too.
  const hasFacetFilter =
    !!selectedCollection ||
    !!selectedCalibre ||
    !!selectedMovementType ||
    gapFilterActive ||
    onlyWithoutImage;

  const isSearchMode = !!activeQuery;

  /**
   * The Created sort, when it is running server-side: a virtual sort replica of
   * the references index. Undefined for every other sort, which the index has no
   * replica for and which therefore still reorders the loaded rows client-side.
   *
   * Undefined while relevance is the active sort — that is what sends the query
   * to the master index, where the ranking lives. Note the replica IS used now
   * when an admin picks Created DURING a search: that is them asking for their
   * matches in date order, which the replica serves across the whole result set
   * rather than only over the loaded page.
   */
  const hitsSort: ReferenceSortKey | undefined =
    !relevanceActive && effectiveSortField === 'createdAt'
      ? sortDir === 'asc'
        ? 'createdAt_asc'
        : 'createdAt_desc'
      : undefined;

  /**
   * The listing is Algolia-backed: one search per page covering browse, search,
   * every filter and the Created sort at once. False only when the index can't
   * be trusted for this brand (see `filterSource`) — it is never an admin
   * choice.
   *
   * The one exception an admin DOES control is the pair of enrichment-gap
   * toggles (`gapFilterActive`), and they are not a preference: those filters
   * are inexpressible in the index (see the state declarations), so they have
   * to be answered from Firestore or not at all.
   */
  const algoliaMode =
    algoliaConfigured &&
    !brand.isDisabled &&
    filterSource === 'algolia' &&
    !gapFilterActive;

  /** How many of `hitsTotal` the index will actually hand back. */
  const hitsReachable = Math.min(hitsTotal, ALGOLIA_MAX_RETRIEVABLE);

  /** Human-readable description of the active filters. */
  const filterLabel = [
    selectedCollection ? `in "${selectedCollection}"` : null,
    selectedCalibre ? `with calibre "${selectedCalibre}"` : null,
    selectedMovementType ? `${selectedMovementType} movement` : null,
    onlyMissingCalibre ? 'without a calibre' : null,
    onlyNeedsAiEnrichment ? 'needing AI enrichment' : null,
    onlyWithoutImage ? 'without an image' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Browse the full in-memory brand set (so no ref on a later page is missed)
  // instead of the paginated browse list. There is nothing left to page in when
  // this is true. Either because a filter is active on the fallback path, or
  // because the "without calibre" toggle put us on that path deliberately.
  const browsingFullBrandSet =
    gapFilterActive || (filterSource === 'firestore' && hasFacetFilter);
  const browseBase = browsingFullBrandSet ? fallbackBrandRefs : references;

  // The Firestore fallback path. Always sorts: nothing ranked these rows, so
  // `effectiveSortField` (never the sentinel) is the only meaningful order.
  const sortedReferences = useMemo(() => {
    const sorted = [...browseBase].sort((a, b) => {
      if (effectiveSortField === 'createdAt') {
        // Refs with no creation date sort last in both directions (they can't be
        // ordered meaningfully). The server page is already in this order; this
        // matters for the calibre path, which sorts the full in-memory set.
        const am = refCreatedAtMs(a);
        const bm = refCreatedAtMs(b);
        if (am === null || bm === null) return am === bm ? 0 : am === null ? 1 : -1;
        return sortDir === 'asc' ? am - bm : bm - am;
      }
      const av = (a[effectiveSortField] ?? '') as string;
      const bv = (b[effectiveSortField] ?? '') as string;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return sorted;
  }, [browseBase, effectiveSortField, sortDir]);

  const filteredReferences = useMemo(() => {
    // Parents and standalones only — enforced SERVER-side now, by the
    // `isVariant == false` filter on both the paginated browse query and the
    // fallback full-brand fetch, so there is no variant left to strip here.
    let result = sortedReferences;
    // On the Algolia listing the filtering happens in the index and the rows
    // render from `hits`, so these client-side passes only ever run on the
    // Firestore listing.
    if (selectedCollection) {
      result = result.filter(r => r.collection === selectedCollection);
    }
    if (selectedCalibre) {
      const target = normalizeCalibreKey(selectedCalibre);
      result = result.filter(r => {
        const name = resolveDisplayCalibre(r);
        return !!name && normalizeCalibreKey(name) === target;
      });
    }
    if (selectedMovementType) {
      const target = selectedMovementType.toLowerCase();
      result = result.filter(r => (r.movementType || '').toLowerCase() === target);
    }
    // Composes with the rest rather than replacing them — ticking it inside a
    // collection narrows to that collection's gaps. Combined with the calibre
    // DROPDOWN it is a contradiction and correctly yields nothing.
    if (onlyMissingCalibre) {
      result = result.filter(referenceHasNoCalibre);
    }
    // Composes the same way: with the calibre toggle it is "refs missing both".
    if (onlyNeedsAiEnrichment) {
      result = result.filter(referenceNeedsAiEnrichment);
    }
    if (onlyWithoutImage) {
      result = result.filter(referenceHasNoImage);
    }
    return result;
  }, [
    sortedReferences,
    selectedCollection,
    selectedCalibre,
    selectedMovementType,
    onlyMissingCalibre,
    onlyNeedsAiEnrichment,
    onlyWithoutImage,
  ]);

  // Parents/standalones on the loaded pages. Every loaded doc is one now that
  // the browse query filters server-side, so a page of PAGE_SIZE is PAGE_SIZE
  // real rows rather than the handful that survived the old client-side strip.
  const loadedParentCount = references.length;

  /**
   * Loaded hits in display order.
   *
   * Relevance leaves them exactly as they arrived — best match first is the
   * whole point, and it is the sort a typed query auto-selects. The Created sort
   * is served by a sort replica, so those hits also arrive already ordered.
   * Every other sort has no replica: the hits arrive in index order and the sort
   * reorders the rows loaded so far, exactly as it has always reordered the
   * loaded Firestore page — including now during a search, which the old
   * blanket `isSearchMode` bail made impossible.
   */
  const sortedHits = useMemo(() => {
    if (relevanceActive || effectiveSortField === 'createdAt') return hits;
    return [...hits].sort((a, b) => {
      const av = (a[effectiveSortField] ?? '') as string;
      const bv = (b[effectiveSortField] ?? '') as string;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [hits, relevanceActive, effectiveSortField, sortDir]);

  // Coverage stats over the loaded rows — parents-only by construction on both
  // paths. Read off whichever source is driving the listing.
  const stats = useMemo(() => {
    if (algoliaMode) {
      return {
        total: hits.length,
        withImages: hits.filter(h => !!h.imageUrl).length,
        withCalibre: hits.filter(h => h.calibre?.trim()).length,
        withMovementType: hits.filter(h => h.movementType?.trim()).length,
      };
    }
    // With the "without calibre" toggle on, the loaded pages are not what's on
    // screen — the whole brand is in memory and the filter is what narrowed it.
    // Counting `references` there would describe rows nobody is looking at (and
    // report a "with calibre" figure that must be 0 by construction).
    const base = gapFilterActive ? filteredReferences : references;
    return {
      total: base.length,
      withImages: base.filter(r => r.images && r.images.length > 0).length,
      withCalibre: base.filter(r => !!resolveDisplayCalibre(r)).length,
      withMovementType: base.filter(r => r.movementType?.trim()).length,
    };
  }, [algoliaMode, hits, references, gapFilterActive, filteredReferences]);

  /**
   * Refs the selection can be satisfied from without a Firestore round trip.
   *
   * The full brand set when one is loaded — that is precisely the case where
   * every selectable row is already a complete document in memory, so a bulk
   * action over 200 ticked rows costs nothing extra. Otherwise the loaded browse
   * page, with the effect below fetching whatever the selection reaches beyond
   * it.
   */
  const selectionPool = browsingFullBrandSet ? fallbackBrandRefs : references;

  const selectedRefs = useMemo(
    () => selectionPool.filter(r => selectedIds.has(r.id)),
    [selectionPool, selectedIds],
  );

  // For search/collection mode, we need to load full docs from Firestore for selected IDs
  // that aren't in the local references array
  const [searchSelectedRefs, setSearchSelectedRefs] = useState<WatchReference[]>([]);
  const [loadingSearchRefs, setLoadingSearchRefs] = useState(false);
  // Combine local refs + search-loaded refs, deduped
  const effectiveSelectedRefs = useMemo(() => {
    const combined = [...selectedRefs];
    const localIds = new Set(selectedRefs.map(r => r.id));
    for (const ref of searchSelectedRefs) {
      if (!localIds.has(ref.id)) combined.push(ref);
    }
    return combined;
  }, [selectedRefs, searchSelectedRefs]);

  // Load full Firestore docs for selected IDs NOT in the local references array
  useEffect(() => {
    if (selectedIds.size === 0) {
      setSearchSelectedRefs([]);
      setLoadingSearchRefs(false);
      return;
    }
    // Find IDs the in-memory pool can't already satisfy
    const localRefIds = new Set(selectionPool.map(r => r.id));
    const missingIds = Array.from(selectedIds).filter(id => !localRefIds.has(id));
    if (missingIds.length === 0) {
      setSearchSelectedRefs([]);
      setLoadingSearchRefs(false);
      return;
    }
    // Load missing refs from Firestore in parallel batches
    let cancelled = false;
    setLoadingSearchRefs(true);
    (async () => {
      const loaded: WatchReference[] = [];
      for (let i = 0; i < missingIds.length; i += 20) {
        if (cancelled) break;
        const batch = missingIds.slice(i, i + 20);
        const results = await Promise.all(
          batch.map(refId =>
            getWatchReference(brand.id, refId).catch(() => null)
          )
        );
        for (const ref of results) {
          if (ref && !cancelled) loaded.push(ref);
        }
      }
      if (!cancelled) {
        setSearchSelectedRefs(loaded);
        setLoadingSearchRefs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedIds, selectionPool, brand.id]);

  /**
   * Ids of the rows currently on screen, whichever source rendered them. An
   * Algolia hit's `referenceId` is the same Firestore document id a
   * `WatchReference.id` carries, so one selection set spans both paths and the
   * bulk action bar doesn't care which listing produced it.
   */
  const visibleIds = useMemo(
    () =>
      algoliaMode
        ? sortedHits.map(h => h.referenceId)
        : filteredReferences.map(r => r.id),
    [algoliaMode, sortedHits, filteredReferences],
  );

  const allFilteredSelected =
    visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));

  const someFilteredSelected = visibleIds.some(id => selectedIds.has(id));

  // Sync indeterminate on select-all checkbox
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someFilteredSelected && !allFilteredSelected;
    }
  }, [someFilteredSelected, allFilteredSelected]);

  // ── Data loading ───────────────────────────────────────────────────────────

  // The Firestore listing is loaded ONLY when it is the one on screen — that is
  // the read saving. On the Algolia path this effect does nothing and the brand
  // opens without touching the references subcollection.
  //
  // The "without calibre" toggle also turns `algoliaMode` off, but it renders
  // from the full brand set rather than the paginated browse list, so paging a
  // browse page in behind it would be a read nothing displays.
  useEffect(() => {
    if (algoliaMode || gapFilterActive) return;
    loadReferences();
  }, [brand.id, browseSortKey, algoliaMode, gapFilterActive]);

  /**
   * The full brand set behind the "without calibre" toggle, fetched lazily.
   *
   * One brand-scoped, parents-only read on the first tick, then cached for the
   * brand — including across untick/retick, which is what stops an admin
   * comparing the filtered and unfiltered views from paying twice. The fallback
   * path fetches the same set for its dropdowns, so `fullBrandSetFor` lets this
   * skip the read entirely when that already happened.
   */
  useEffect(() => {
    if (!gapFilterActive) return;
    const generation = `${brand.id}:${fullBrandSetNonce}`;
    if (fullBrandSetFor.current === generation) return;
    let cancelled = false;
    setLoadingFullBrandSet(true);
    getReferencesForBrand(brand.id, FULL_BRAND_SET_LIMIT, { parentsOnly: true })
      .then(parents => {
        if (cancelled) return;
        fullBrandSetFor.current = generation;
        setFallbackBrandRefs(parents);
        setFullBrandSetTruncated(parents.length >= FULL_BRAND_SET_LIMIT);
        console.info(
          `[refBrowser] enrichment-gap filter: ${parents.length} refs read`,
        );
      })
      .catch(err => {
        if (cancelled) return;
        // Leave the toggle on but the set empty: the listing then shows its
        // "no references match" state, which is honest, and a retick retries.
        console.error('Failed to load the brand set for the enrichment-gap filter:', err);
        toast.error('Failed to load references for the filter');
      })
      .finally(() => {
        if (!cancelled) setLoadingFullBrandSet(false);
      });
    return () => { cancelled = true; };
  }, [gapFilterActive, brand.id, fullBrandSetNonce]);

  // Whole-subcollection count (parents, standalones AND variants) for the
  // delete-all flow. An aggregation query — no documents downloaded — so it runs
  // on both paths, and it is the only Firestore call the Algolia listing makes.
  useEffect(() => {
    let cancelled = false;
    getReferencesCountForBrand(brand.id)
      .then(count => { if (!cancelled) setTotalCount(count); })
      .catch(err => {
        // Non-fatal: only leaves the delete-all modal's number stale.
        console.error('Failed to load total reference count:', err);
      });
    return () => { cancelled = true; };
  }, [brand.id]);

  // Filter dropdowns + the parents-only count for this brand. Reset the derived
  // state on brand change, then (re)load. Also re-run after a bulk enrich write
  // so newly-written collection/calibre values show up as filter options without
  // a remount.
  useEffect(() => {
    // Re-optimism on every brand change: one brand falling back to Firestore
    // (disabled, unindexed, failed search) must not condemn the next brand to
    // the Firestore listing too. `loadFilterOptions` sets it back if warranted.
    setFilterSource('algolia');
    setSearchError(null);
    setSelectedCollection('');
    setSelectedCalibre('');
    setSelectedMovementType('');
    setOnlyMissingCalibre(false);
    setOnlyWithoutImage(false);
    // Cleared, not left showing: a count is a claim about THIS brand, and the
    // previous brand's number sitting there until the facets land would be one.
    setNoImageCount(null);
    setCollectionOptions([]);
    setCalibreOptions([]);
    setMovementTypeOptions([]);
    setFallbackBrandRefs([]);
    // The cached set belongs to the brand that was open, so the marker has to
    // go with it — otherwise the next brand's toggle would filter the previous
    // brand's references.
    fullBrandSetFor.current = null;
    setFullBrandSetTruncated(false);
    setParentCount(0);
    loadFilterOptions();
  }, [brand.id, brand.isDisabled, algoliaConfigured]);

  // The Algolia listing: browse, search and filters in one query. Re-runs on any
  // input change and always restarts at page 0; Load More appends from there.
  useEffect(() => {
    if (!algoliaMode) {
      setHits([]);
      setHitsTotal(0);
      hitsPageRef.current = 0;
      return;
    }
    let cancelled = false;
    hitsPageRef.current = 0;
    setLoadingHits(true);
    setSearchError(null);
    fetchHitsPage(0)
      .then(result => {
        if (cancelled) return;
        setHits(result.hits);
        setHitsTotal(result.nbHits);
        console.info(
          `[refBrowser] Algolia: ${result.nbHits} hits ` +
            `(page ${result.page}/${Math.max(0, result.nbPages - 1)}), ` +
            '0 Firestore reads',
        );
      })
      .catch(err => {
        if (cancelled) return;
        // Drop to Firestore rather than showing an empty catalogue: the listing
        // effect above picks up as soon as `filterSource` flips.
        console.error('Reference listing search failed, using Firestore:', err);
        setSearchError(
          err instanceof Error ? err.message : 'Search failed — showing Firestore results',
        );
        setHits([]);
        setHitsTotal(0);
        setFilterSource('firestore');
      })
      .finally(() => {
        if (!cancelled) setLoadingHits(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    algoliaMode,
    brand.id,
    activeQuery,
    selectedCollection,
    selectedCalibre,
    selectedMovementType,
    onlyWithoutImage,
    // The Created sort is served by a different (replica) index, so changing it
    // is a new query, not a client-side reorder.
    hitsSort,
  ]);

  useEffect(() => {
    if (updatedReference) {
      setReferences((prev) =>
        prev.map((r) => (r.id === updatedReference.id ? updatedReference : r)),
      );
    }
  }, [updatedReference]);

  useEffect(() => {
    if (deletedReferenceId) {
      setReferences((prev) => prev.filter((r) => r.id !== deletedReferenceId));
      setHits((prev) => prev.filter((h) => h.referenceId !== deletedReferenceId));
      setHitsTotal((prev) => Math.max(0, prev - 1));
      setTotalCount((prev) => Math.max(0, prev - 1));
      // Only parents/standalones are listed, so anything deleted from here was
      // one. Adjusted optimistically because the Algolia count behind it won't
      // reflect the delete until syncReferenceToAlgolia has fired.
      setParentCount((prev) => Math.max(0, prev - 1));
    }
  }, [deletedReferenceId]);

  const loadReferences = async () => {
    setLoading(true);
    lastDocRef.current = null;
    try {
      const result = await getReferencesForBrandPaginated(
        brand.id,
        PAGE_SIZE,
        null,
        browseSort,
        { parentsOnly: true },
      );
      setReferences(result.references);
      setHasMore(result.hasMore);
      lastDocRef.current = result.lastDoc;
      console.info(
        `[refBrowser] Firestore fallback: ${result.references.length} refs read`,
      );
    } catch (error) {
      console.error('Error loading references:', error);
      toast.error('Failed to load references');
    } finally {
      setLoading(false);
    }
  };

  /**
   * One page of the Algolia listing — the typed query plus every active filter,
   * scoped to this brand.
   *
   * The calibre filter sends the selected option's `rawValues` as an OR group
   * rather than the display string: the dropdown folds case/diacritic variants
   * into one entry, so "ETA 2824" and "eta 2824" must both match. Sending the
   * exact stored values sidesteps any dependence on Algolia's facet-matching
   * case rules.
   */
  const fetchHitsPage = (page: number) => {
    const calibre = calibreOptions.find((c) => c.value === selectedCalibre);
    // If the options are mid-reload (a post-enrich refresh clears and refetches
    // them), fall back to the selected display string so the filter is never
    // silently dropped and an unfiltered page returned.
    const calibreValues = calibre
      ? calibre.rawValues
      : selectedCalibre
        ? [selectedCalibre]
        : [];
    return searchReferences(
      activeQuery,
      {
        brandId: brand.id,
        collection: selectedCollection || undefined,
        calibreValues,
        movementType: selectedMovementType || undefined,
        onlyWithoutImage,
      },
      page,
      LISTING_PAGE_SIZE,
      hitsSort,
    );
  };

  /**
   * Load the Collection + Calibre + Movement dropdown options and the brand's
   * parents-only count.
   *
   * Preferred path is ONE brand-scoped Algolia call at `hitsPerPage: 0` — no
   * records downloaded, no Firestore reads — returning all three facet maps and
   * `nbHits` together. The index holds parents and standalones only, so the
   * values and counts are variant-free by construction. This replaces a
   * `getReferencesForBrand(brandId, 10000)` fetch of every full reference
   * document that ran on every brand open, and folds together the three
   * separate searches that had replaced it.
   *
   * Falls back to that Firestore fetch when Algolia can't be trusted for this
   * brand:
   *   - Algolia isn't configured;
   *   - the brand is DISABLED — syncReferenceToAlgolia deletes every ref of a
   *     disabled brand from the index, so it would report an empty catalogue for
   *     a brand that is exactly when an admin needs to see its references;
   *   - the call throws;
   *   - the index returns ZERO references. That is either a genuinely empty
   *     brand or an unsynced/lagging index, and Firestore settles it. Costs
   *     nothing when the brand really is empty, and avoids reporting "0
   *     references" over a populated grid.
   *
   * The request token ensures a slow load can't overwrite a newer one (rapid
   * brand switch, or a post-enrich refresh firing while a brand-change load is
   * still in flight). Existing options are deliberately NOT cleared here — the
   * brand-change effect does that, so a post-enrich refresh leaves the dropdowns
   * populated while it re-reads rather than blinking through empty.
   */
  const loadFilterOptions = async () => {
    const reqId = ++filterOptionsReqId.current;
    const stale = () => reqId !== filterOptionsReqId.current;

    if (algoliaConfigured && !brand.isDisabled) {
      try {
        const facets = await getBrandListingFacets(brand.id);
        if (stale()) return;
        if (facets.count > 0) {
          setFilterSource('algolia');
          setParentCount(facets.count);
          setCollectionOptions(facets.collection);
          setCalibreOptions(facets.calibre);
          setMovementTypeOptions(facets.movementType);
          setNoImageCount(facets.noImageCount);
          // Held onto while the "without calibre" filter is running off it: that
          // filter has no index-side equivalent, so dropping the set here would
          // empty a listing the admin is part-way through working.
          if (!gapFilterActive) {
            setFallbackBrandRefs([]);
            fullBrandSetFor.current = null;
            setFullBrandSetTruncated(false);
          }
          return;
        }
      } catch (err) {
        if (stale()) return;
        console.error('Failed to load brand filter options from Algolia:', err);
      }
    }

    if (stale()) return;
    setFilterSource('firestore');
    try {
      // Variants are excluded server-side, so every count and dropdown option
      // derived from this set is parents-only — matching what the listing
      // shows. A collection or calibre that only ever appears on variants
      // therefore stops being offered as a filter, which is the intent.
      //
      // Filtering in the query rather than after it also stops the 10k cap
      // being spent on variant docs: on a variant-heavy brand that previously
      // truncated the PARENTS, silently under-reporting the catalogue on
      // exactly the path disabled brands always take.
      const parents = await getReferencesForBrand(brand.id, FULL_BRAND_SET_LIMIT, {
        parentsOnly: true,
      });
      if (stale()) return;

      const collectionCounts = new Map<string, BrandFacetValue>();
      const calibreCounts = new Map<string, BrandFacetValue>();
      const movementCounts = new Map<string, BrandFacetValue>();
      for (const ref of parents) {
        const mov = ref.movementType?.trim();
        if (mov) {
          const movKey = mov.toLowerCase();
          const existingMov = movementCounts.get(movKey);
          if (existingMov) {
            existingMov.count += 1;
            if (!existingMov.rawValues.includes(mov)) existingMov.rawValues.push(mov);
          } else {
            movementCounts.set(movKey, { value: mov, count: 1, rawValues: [mov] });
          }
        }
        const col = ref.collection?.trim();
        if (col) {
          const existing = collectionCounts.get(col);
          if (existing) existing.count += 1;
          else collectionCounts.set(col, { value: col, count: 1, rawValues: [col] });
        }
        const cal = resolveDisplayCalibre(ref);
        if (cal) {
          // Case-insensitive dedup, first spelling wins for display — the same
          // fold the Algolia path applies via `foldCase`.
          const key = cal.toLowerCase();
          const existing = calibreCounts.get(key);
          if (existing) {
            existing.count += 1;
            if (!existing.rawValues.includes(cal)) existing.rawValues.push(cal);
          } else {
            calibreCounts.set(key, { value: cal, count: 1, rawValues: [cal] });
          }
        }
      }
      const byValue = (a: BrandFacetValue, b: BrandFacetValue) =>
        a.value.localeCompare(b.value);
      setCollectionOptions(Array.from(collectionCounts.values()).sort(byValue));
      setCalibreOptions(Array.from(calibreCounts.values()).sort(byValue));
      setMovementTypeOptions(Array.from(movementCounts.values()).sort(byValue));
      setNoImageCount(parents.filter(referenceHasNoImage).length);
      setParentCount(parents.length);
      setFallbackBrandRefs(parents);
      // Same set the "without calibre" toggle wants, so mark it loaded and let
      // that filter run off this read instead of making its own.
      fullBrandSetFor.current = `${brand.id}:${fullBrandSetNonceRef.current}`;
      setFullBrandSetTruncated(parents.length >= FULL_BRAND_SET_LIMIT);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load brand filter options:', err);
    }
  };

  // Refresh both the paginated browse list and the dropdown source data after a
  // bulk enrich write commits, so the grid and the filter options stay in sync.
  //
  // On the Algolia path the facet reload races syncReferenceToAlgolia: a bulk
  // enrich fires one trigger per written ref, so freshly-written collection and
  // calibre values may not be indexed yet and can be missing from the dropdowns
  // for a few seconds. The grid itself reads Firestore and is immediately
  // correct; reopening the brand picks up the rest.
  const refreshAfterEnrich = () => {
    if (algoliaMode) {
      // Re-run the listing search by resetting to page 0 through the same
      // effect the filters use; the search index lags the write by a few
      // seconds, so this may still show pre-enrich values on the first pass.
      fetchHitsPage(0)
        .then(result => {
          hitsPageRef.current = 0;
          setHits(result.hits);
          setHitsTotal(result.nbHits);
        })
        .catch(err => console.error('Failed to refresh listing:', err));
    } else if (!gapFilterActive) {
      loadReferences();
    }
    // Invalidate the cached brand set so a gap filter re-reads it: an enrich
    // that just filled a ref's calibre has to take that ref OFF the list, and
    // nothing else would make it re-fetch. Reads Firestore, so it is immediately
    // correct rather than waiting on the Algolia trigger.
    bumpFullBrandSet();
    loadFilterOptions();
  };

  /**
   * The toolbar Refresh button: re-read the current view from its source without
   * touching the filters, the search, the sort or the scroll position.
   *
   * Covers the same ground as `refreshAfterEnrich` — the listing, the gap
   * filters' cached brand set, and the facets that drive the dropdowns and the
   * no-image count — with one difference that matters for a bulk-cleanup loop:
   * on the Algolia listing it re-fetches EVERY page already loaded rather than
   * collapsing to page 0, so an admin three Load-Mores deep stays three deep.
   *
   * The Firestore fallback reloads its first page (cursor pagination can't be
   * re-walked cheaply), which is what `refreshAfterEnrich` already does there.
   *
   * The search index is written by a per-reference trigger and lags a write by a
   * few seconds, so a refresh straight after a bulk delete can still show some
   * of what was deleted — the button says so on hover.
   */
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      if (algoliaMode) {
        const depth = hitsPageRef.current;
        const pages = await Promise.all(
          Array.from({ length: depth + 1 }, (_, page) => fetchHitsPage(page)),
        );
        setHits(mergeRefreshedHitPages(pages));
        setHitsTotal(pages[0].nbHits);
        hitsPageRef.current = depth;
      } else if (!gapFilterActive) {
        await loadReferences();
      }
      // A gap filter reads the cached brand set; invalidate it so a ref fixed
      // since it was read comes off the list.
      bumpFullBrandSet();
      await loadFilterOptions();
      toast.success('References refreshed');
    } catch (err) {
      console.error('Failed to refresh references:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to refresh references');
    } finally {
      setRefreshing(false);
    }
  };

  const loadMoreHits = async () => {
    // Not while a refresh is re-fetching the loaded pages: the page appended here
    // would be overwritten when the refresh lands.
    if (refreshing || loadingMoreHits || hits.length >= hitsReachable) return;
    setLoadingMoreHits(true);
    try {
      const nextPage = hitsPageRef.current + 1;
      const result = await fetchHitsPage(nextPage);
      hitsPageRef.current = nextPage;
      setHits(prev => [...prev, ...result.hits]);
      setHitsTotal(result.nbHits);
      console.info(
        `[refBrowser] Algolia: ${result.nbHits} hits ` +
          `(page ${result.page}/${Math.max(0, result.nbPages - 1)}), ` +
          '0 Firestore reads',
      );
    } catch (error) {
      console.error('Error loading more references:', error);
      toast.error('Failed to load more references');
    } finally {
      setLoadingMoreHits(false);
    }
  };

  const loadMoreReferences = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await getReferencesForBrandPaginated(
        brand.id,
        PAGE_SIZE,
        lastDocRef.current,
        browseSort,
        { parentsOnly: true },
      );
      setReferences(prev => [...prev, ...result.references]);
      setHasMore(result.hasMore);
      lastDocRef.current = result.lastDoc;
    } catch (error) {
      console.error('Error loading more references:', error);
      toast.error('Failed to load more references');
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Search ─────────────────────────────────────────────────────────────────

  /**
   * Flush the debounce. Typing already searches, so the button and Enter key
   * exist only to skip the wait — and to tell the admin when searching isn't
   * possible at all (Algolia unconfigured, or this brand has fallen back to the
   * Firestore listing, which has no text search).
   */
  const handleSearch = () => {
    if (!algoliaConfigured) {
      toast.error('Search is not configured. Please set up Algolia.');
      return;
    }
    if (!algoliaMode) {
      toast.error(
        'This brand is not in the search index, so text search is unavailable.',
      );
      return;
    }
    setActiveQuery(searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveQuery('');
    setSelectedIds(new Set());
    setSelectAllBrand(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSearchResultClick = async (hit: WatchReferenceAlgoliaHit) => {
    setLoadingReferenceId(hit.referenceId);
    try {
      const fullRef = await getWatchReference(hit.brandId, hit.referenceId);
      if (fullRef) {
        onSelectReference(fullRef);
      } else {
        toast.error('Reference not found');
      }
    } catch (error) {
      console.error('Error loading reference:', error);
      toast.error('Failed to load reference');
    } finally {
      setLoadingReferenceId(null);
    }
  };

  // ── Sort ───────────────────────────────────────────────────────────────────

  // Every click here is a manual pick, which is what keeps a later query from
  // auto-switching back to relevance. Relevance itself has no direction.
  const toggleSort = (field: WithRelevance<SortField>) => {
    if (field === RELEVANCE_SORT) {
      pickSort(RELEVANCE_SORT);
      return;
    }
    if (effectiveSortField === field && !relevanceActive) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      pickSort(field);
    } else {
      pickSort(field);
      // Newest-first is the useful default for "Created" (what did the last
      // import add?); the text fields read better A→Z.
      setSortDir(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  // ── Multi-select ───────────────────────────────────────────────────────────

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    const wasAllSelected = allFilteredSelected;
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (wasAllSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => { setSelectedIds(new Set()); setSelectAllBrand(false); };

  // ── Select all for brand ──────────────────────────────────────────────────

  const handleSelectAllBrand = async () => {
    setSelectAllBrand(true);
    try {
      // Parents/standalones only — bulk actions launched from this listing must
      // never silently reach a variant the admin can't see here. Excluded in
      // the query so the 10k cap is spent entirely on selectable rows.
      const parents = await getReferencesForBrand(brand.id, 10000, {
        parentsOnly: true,
      });
      setSelectedIds(new Set(parents.map(r => r.id)));
      toast.success(`Selected all ${parents.length} references for ${brand.displayName || brand.name}`);
    } catch (err) {
      console.error('Failed to select all brand references:', err);
      toast.error('Failed to load all references');
      setSelectAllBrand(false);
    }
  };

  // ── Delete ALL refs for this brand (full wipe) ─────────────────────────────

  const brandLabel = brand.displayName || brand.name;

  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    setDeleteAllProgress(0);
    setDeleteAllTotal(totalCount);
    try {
      const deleted = await deleteAllReferencesForBrand(brand.id, (done, total) => {
        setDeleteAllProgress(done);
        setDeleteAllTotal(total);
      });
      // Clear local state and pull a fresh (empty) page. `parentCount` is zeroed
      // rather than reloaded: Algolia is still unwinding one delete trigger per
      // wiped ref, so re-reading nbHits here would report a stale non-zero
      // catalogue. The dropdowns are cleared for the same reason.
      setReferences([]);
      setHits([]);
      setHitsTotal(0);
      setTotalCount(0);
      setParentCount(0);
      setCollectionOptions([]);
      setCalibreOptions([]);
      setMovementTypeOptions([]);
      setFallbackBrandRefs([]);
      fullBrandSetFor.current = null;
      setFullBrandSetTruncated(false);
      setSelectedCollection('');
      setSelectedCalibre('');
      setSelectedMovementType('');
      setOnlyMissingCalibre(false);
      setOnlyWithoutImage(false);
      // Zero, matching `parentCount` above — the brand has just been emptied.
      setNoImageCount(0);
      setSelectedIds(new Set());
      setSelectAllBrand(false);
      setShowDeleteAllModal(false);
      setDeleteAllConfirmText('');
      toast.success(`Deleted all ${deleted.toLocaleString()} reference${deleted !== 1 ? 's' : ''} for ${brandLabel}`);
      if (!algoliaMode) await loadReferences();
    } catch (error) {
      console.error('Error deleting all references:', error);
      toast.error('Failed to delete all references — some may remain. Try again.');
    } finally {
      setIsDeletingAll(false);
    }
  };

  // ── Create ─────────────────────────────────────────────────────────────────

  const resetCreateForm = () => {
    setNewReference('');
    setNewModel('');
    setNewCollection('');
    setNewProductName('');
    setIsCreating(false);
  };

  const handleCreate = async () => {
    // Mirror the service-level invariant: the ref must normalize to a non-empty
    // value (not blank, whitespace-only, or all non-alphanumeric like "---").
    if (!normalizeReference(newReference)) {
      toast.error('Reference must contain at least one alphanumeric character');
      return;
    }
    setIsProcessing(true);
    try {
      const searchText = computeSearchText(
        brand.name,
        newReference.trim(),
        newModel.trim(),
        undefined,
        undefined,
        undefined,
        newCollection.trim(),
      );
      const referenceId = await createWatchReference(brand.id, {
        reference: newReference.trim(),
        model: newModel.trim(),
        collection: newCollection.trim() || '',
        productName: newProductName.trim() || '',
        searchText,
      });
      const newRef: WatchReference = {
        id: referenceId,
        brandId: brand.id,
        reference: newReference.trim(),
        model: newModel.trim(),
        collection: newCollection.trim() || '',
        productName: newProductName.trim() || '',
        searchText,
      };
      setReferences((prev) =>
        [...prev, newRef].sort((a, b) => (a.reference || '').localeCompare(b.reference || '')),
      );
      // The Algolia listing won't see it until syncReferenceToAlgolia fires, so
      // put it on screen optimistically rather than appearing to have dropped
      // the ref that was just created.
      setHits((prev) => [
        {
          objectID: referenceId,
          brandId: brand.id,
          referenceId,
          reference: newRef.reference,
          model: newRef.model || '',
          collection: newRef.collection,
          productName: newRef.productName,
        },
        ...prev,
      ]);
      setHitsTotal((prev) => prev + 1);
      toast.success('Reference created successfully');
      resetCreateForm();
    } catch (error) {
      console.error('Error creating reference:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create reference');
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Only the Firestore LISTING blocks the whole panel while it loads; the
  // Algolia listing renders the chrome immediately and spins over the grid, so
  // the search box and filters stay usable.
  //
  // The gap filters (`gapFilterActive`) are excluded deliberately. `loading` starts true and is
  // only ever cleared by `loadReferences`, which the Algolia path never calls —
  // so a brand opened on the index and then filtered for calibre gaps would hit
  // this gate with a `loading` that nothing is going to clear, blanking the
  // panel (and the checkbox needed to undo it) for good. That path spins over
  // the grid instead, on `loadingFullBrandSet`.
  if (loading && !algoliaMode && !gapFilterActive) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <BrandLogo
            src={brand.logo}
            name={brand.displayName || brand.name}
            size={48}
            lightArtwork={brand.logoHeroTransparent === true}
          />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900">{brand.displayName || brand.name}</h2>
              {onEditBrand && (
                <button
                  onClick={onEditBrand}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Edit brand details"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
            </div>
            {/* Brand info line */}
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              {brand.country && (
                <span className="text-xs text-gray-500">
                  {COUNTRY_FLAG[brand.country.toLowerCase()] && `${COUNTRY_FLAG[brand.country.toLowerCase()]} `}{brand.country}
                </span>
              )}
              {brand.mainWebsite && (
                <span className="text-xs text-gray-400">
                  {brand.country && <span className="mr-2 text-gray-300">·</span>}
                  {brand.mainWebsite.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </span>
              )}
              {brand.parentOrg && (
                <span className="text-xs text-gray-400">
                  {(brand.country || brand.mainWebsite) && <span className="mr-2 text-gray-300">·</span>}
                  {brand.parentOrg}
                </span>
              )}
              {hasShopifySource && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                  Shopify
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {algoliaMode
                ? [
                    `${hits.length} of ${hitsTotal.toLocaleString()} references`,
                    isSearchMode ? `matching "${activeQuery}"` : null,
                    filterLabel || null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : `${loadedParentCount} of ${parentCount} references`}
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {totalCount > 0 && (
            <button
              onClick={() => { setDeleteAllConfirmText(''); setShowDeleteAllModal(true); }}
              className="text-xs text-red-600 hover:text-red-700 hover:underline px-1.5 py-1 transition-colors"
              title="Delete every reference for this brand (for re-import migration)"
            >
              Delete all refs
            </button>
          )}
          {onOpenBulkImport ? (
            <Button variant="secondary" onClick={() => onOpenBulkImport(brand.id)}>
              Bulk Import
            </Button>
          ) : (
            <BulkImport brand={brand} onComplete={loadReferences} onBrandUpdated={onBrandUpdated} />
          )}
          {hasShopifySource && <ProbeShopify brand={brand} />}
          {canRunScraper(extractorJobsEnabled, scraperCatalogue, brand, scraperBrandOverrides) && (
            <Button variant="secondary" onClick={() => setShowRunScraper(true)}>
              ⚡ Run scraper
            </Button>
          )}
          <RetryMissingImages brand={brand} onComplete={loadReferences} />
          <BackfillProductUrls onComplete={loadReferences} />
          <Button onClick={() => setIsCreating(true)}>+ Add Reference</Button>
        </div>
      </div>

      {/* Per-brand post-import enrichment tasks */}
      <EnrichmentTasksPanel brand={brand} onTaskCompleted={refreshAfterEnrich} />

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative w-full sm:flex-1">
          <input
            type="text"
            placeholder="Search by reference, model, product name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSearch} loading={loadingHits && isSearchMode}>Search</Button>
          {(searchTerm || isSearchMode) && (
            <Button variant="secondary" onClick={handleClearSearch}>Clear</Button>
          )}
        </div>
      </div>

      {/* Search error */}
      {searchError && (
        <div className="text-red-600 text-sm bg-red-50 p-3 rounded">
          Search error: {searchError}
        </div>
      )}

      {/* Coverage stats over the loaded rows. Rendered on both listing sources —
          the Algolia hits carry imageUrl/calibre/movementType, so the same four
          pills are computable without full documents. */}
      {stats.total > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          {algoliaMode && (isSearchMode || hasFacetFilter) && (
            <StatPill label="matching" value={hitsTotal} accent="blue" />
          )}
          <StatPill label="loaded" value={stats.total} />
          <StatPill label="with images" value={stats.withImages} accent="blue" />
          <StatPill label="with calibre" value={stats.withCalibre} accent="emerald" />
          <StatPill label="with movement type" value={stats.withMovementType} accent="amber" />
        </div>
      )}

      {/* Say it out loud when the gap filter is reading a capped set, rather
          than reporting a count that quietly means "of the first 10,000". */}
      {gapFilterActive && fullBrandSetTruncated && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
          This brand has at least {FULL_BRAND_SET_LIMIT.toLocaleString()} parent
          references. The enrichment-gap filters read the first{' '}
          {FULL_BRAND_SET_LIMIT.toLocaleString()} of them, so anything past that
          is neither listed nor counted here.
        </div>
      )}

      {/* Browse controls: select-all + sort + filters + source + view toggle.
          Shown in every mode now that search, filters and browse are one
          listing that combines them. */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1.5 cursor-pointer mr-1">
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allFilteredSelected}
            onChange={handleSelectAll}
            className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
          />
          <span className="text-xs text-gray-500">Select all</span>
        </label>
        <span className="text-gray-200">|</span>

        {/* Sort buttons. Relevance joins them only while a query is running —
            there is nothing ranked without one — and the rest are no longer
            disabled during a search, so an admin can now sort their matches. */}
        <span className="text-xs text-gray-500">Sort:</span>
        {([
          ...(relevanceAvailable ? ([RELEVANCE_SORT] as const) : []),
          'reference',
          'model',
          'collection',
          'createdAt',
        ] as const).map((field) => {
          const active =
            field === RELEVANCE_SORT
              ? relevanceActive
              : !relevanceActive && effectiveSortField === field;
          return (
            <button
              key={field}
              onClick={() => toggleSort(field)}
              className={`px-2 py-1 rounded border text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                active
                  ? 'border-brand-400 bg-brand-50 text-brand-700 font-medium'
                  : 'border-gray-200 hover:border-gray-300 text-gray-500'
              }`}
              title={
                field === RELEVANCE_SORT
                  ? 'Best match first, as the index ranked it. Picked automatically when you type a search.'
                  : field === 'createdAt'
                    ? 'Date the reference was added — newest first shows the latest import. Sorted in the index, across the whole brand.'
                    : undefined
              }
            >
              {field === RELEVANCE_SORT ? '✨ ' : ''}
              {SORT_LABELS[field]}
              {active && field !== RELEVANCE_SORT && (
                <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
              )}
            </button>
          );
        })}

        <span className="text-gray-200">|</span>

        {/* Collection filter */}
        <select
          value={selectedCollection}
          onChange={(e) => { setSelectedCollection(e.target.value); setSelectedIds(new Set()); setSelectAllBrand(false); }}
          className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
        >
          <option value="">All collections ({parentCount})</option>
          {/* Brand-scoped Algolia facet counts — every collection across the
              brand's parents/standalones, independent of the browse page. On
              the fallback path the same options are derived from the one-shot
              Firestore fetch. */}
          {collectionOptions.map(({ value, count }) => (
            <option key={value} value={value}>
              {value} ({count})
            </option>
          ))}
        </select>

        {/* Calibre filter — distinct calibre names for this brand, deduped
            case-insensitively. Selecting one filters in Algolia (or, on the
            fallback path, client-side over the full in-memory set). */}
        <select
          value={selectedCalibre}
          onChange={(e) => { setSelectedCalibre(e.target.value); setSelectedIds(new Set()); setSelectAllBrand(false); }}
          className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
          title="Filter references by calibre"
        >
          <option value="">All calibres ({calibreOptions.length})</option>
          {calibreOptions.map(({ value, count }) => (
            <option key={value} value={value}>
              {value} ({count})
            </option>
          ))}
        </select>

        {/* Movement type filter — an indexed facet, so its options and counts
            arrive in the same response as the other two dropdowns. */}
        <select
          value={selectedMovementType}
          onChange={(e) => { setSelectedMovementType(e.target.value); setSelectedIds(new Set()); setSelectAllBrand(false); }}
          className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 bg-white"
          title="Filter references by movement type"
        >
          <option value="">All movements ({movementTypeOptions.length})</option>
          {movementTypeOptions.map(({ value, count }) => (
            <option key={value} value={value}>
              {MOVEMENT_TYPE_LABELS[value] || value} ({count})
            </option>
          ))}
        </select>

        {/* The enrichment-gap filter. Composes with the three dropdowns rather
            than replacing them, but unlike them it cannot run in the index — see
            `onlyMissingCalibre` — so ticking it moves the listing onto a
            one-shot Firestore read of the brand. */}
        <label
          className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer"
          title="Show only references with neither a linked calibre nor a legacy calibre string. Reads the brand from Firestore — the search index can't answer this one."
        >
          <input
            type="checkbox"
            checked={onlyMissingCalibre}
            onChange={(e) => {
              setOnlyMissingCalibre(e.target.checked);
              setSelectedIds(new Set());
              setSelectAllBrand(false);
            }}
            className="w-3.5 h-3.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
          />
          Only refs without calibre
          {loadingFullBrandSet && onlyMissingCalibre && <span className="text-gray-400">…</span>}
        </label>

        {/* The description-gap filter. Same constraint and same path as the
            calibre one beside it — see `onlyNeedsAiEnrichment`. Ticking both
            costs one read, not two: they share the brand set. */}
        <label
          className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer"
          title="Show only references whose description has not been AI-rewritten or hand-written — imported text and legacy refs with no provenance. Reads the brand from Firestore — the search index can't answer this one."
        >
          <input
            type="checkbox"
            checked={onlyNeedsAiEnrichment}
            onChange={(e) => {
              setOnlyNeedsAiEnrichment(e.target.checked);
              setSelectedIds(new Set());
              setSelectAllBrand(false);
            }}
            className="w-3.5 h-3.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
          />
          Only refs needing AI enrichment
          {loadingFullBrandSet && onlyNeedsAiEnrichment && (
            <span className="text-gray-400">…</span>
          )}
        </label>

        {/* The picture-gap filter. An index facet, so unlike the calibre toggle
            beside it this stays on the Algolia listing. The chip counts the
            whole brand, not the current narrowed view, so it reads the same
            whichever other filters are set — it answers "how much cleanup does
            this brand need", which is the question it is for. */}
        <label
          className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer"
          title="Show only references with no image assigned. Counted across the whole brand."
        >
          <input
            type="checkbox"
            checked={onlyWithoutImage}
            onChange={(e) => {
              setOnlyWithoutImage(e.target.checked);
              setSelectedIds(new Set());
              setSelectAllBrand(false);
            }}
            className="w-3.5 h-3.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
          />
          Only refs without image
          {noImageCount !== null && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                noImageCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {noImageCount.toLocaleString()}
            </span>
          )}
        </label>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Same control as every other listing's refresh (Calibres, Winders,
            Modules, Users): a secondary text button with the shared spinner. */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void handleRefresh()}
          loading={refreshing}
          disabled={refreshing || loadingHits || loadingMoreHits}
          title="Reload this view — filters, search, sort and loaded pages are kept. The search index can take a few seconds to catch up after a bulk change."
        >
          Refresh
        </Button>

        {/* Grid / List toggle */}
        <div className="flex border border-gray-200 rounded overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 ${viewMode === 'grid' ? 'bg-brand-50 text-brand-600' : 'text-gray-400 hover:text-gray-600'}`}
            title="Grid view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 border-l border-gray-200 ${viewMode === 'list' ? 'bg-brand-50 text-brand-600' : 'text-gray-400 hover:text-gray-600'}`}
            title="List view"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Fallback listing only — this brand isn't served by the index, so the
          dropdowns narrow whatever is loaded rather than the whole brand. */}
      {!algoliaMode && !browsingFullBrandSet && hasFacetFilter && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
          This brand is not served by the search index, so the filters apply to
          the {references.length.toLocaleString()} loaded row
          {references.length === 1 ? '' : 's'} only — Load More to widen it.
        </div>
      )}

      {/* Firestore orders only docs that carry the orderBy field, so refs with
          no creation date drop out of a Created-sorted page entirely. Only
          reachable on the fallback listing now that the index sorts by date. */}
      {!algoliaMode && !browsingFullBrandSet && effectiveSortField === 'createdAt' && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
          Sorted by creation date against Firestore (this brand is not served by
          the search index), so references without one — older imports — are not
          listed. Switch to <strong>Reference</strong> to see all {parentCount.toLocaleString()}.
        </div>
      )}

      {/* Reference / Model / Collection have no sort replica on the index, so
          those sorts only reorder what is loaded rather than the whole brand.
          Say so instead of letting the sort buttons imply otherwise. Created IS
          server-sorted, and relevance is the index's own order, hence both
          exclusions. Shown during a search too now — those sorts are selectable
          there, and the caveat applies to the loaded MATCHES just the same. */}
      {algoliaMode && !relevanceActive && effectiveSortField !== 'createdAt' && (
        <div className="text-xs text-gray-400">
          {SORT_LABELS[effectiveSortField]} reorders the{' '}
          {hits.length.toLocaleString()} loaded row
          {hits.length === 1 ? '' : 's'}, not the whole{' '}
          {isSearchMode ? 'result set' : 'brand'} — Load More to widen it, or
          sort by <strong>Created</strong>, which is ordered across the full
          catalogue.
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="text-xs text-brand-600 font-medium">
          {selectedIds.size} selected
        </div>
      )}

      {/* References display */}
      {algoliaMode ? (
        loadingHits ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedHits.map((hit) => (
              <SearchResultCard
                key={hit.objectID}
                hit={hit}
                badge={isSearchMode ? 'Search' : undefined}
                onClick={() => handleSearchResultClick(hit)}
                loading={loadingReferenceId === hit.referenceId}
                selected={selectedIds.has(hit.referenceId)}
                onSelect={() => toggleSelect(hit.referenceId)}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                      }}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-2 w-14 text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Collection</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Calibre</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Case Size</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Movement</th>
                </tr>
              </thead>
              <tbody>
                {sortedHits.map((hit) => (
                  <HitRow
                    key={hit.objectID}
                    hit={hit}
                    onClick={() => handleSearchResultClick(hit)}
                    loading={loadingReferenceId === hit.referenceId}
                    selected={selectedIds.has(hit.referenceId)}
                    onSelect={() => toggleSelect(hit.referenceId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : loadingFullBrandSet ? (
        // The gap filter's one-shot brand read. Spins over the grid rather than
        // through the panel-wide `loading` gate, so the checkbox that started it
        // stays on screen to be unticked.
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredReferences.map((ref) => (
            <ReferenceCard
              key={ref.id}
              reference={ref}
              onClick={() => onSelectReference(ref)}
              selected={selectedIds.has(ref.id)}
              onSelect={() => toggleSelect(ref.id)}
            />
          ))}
        </div>
      ) : (
        // List view
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                    }}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                  />
                </th>
                <th className="px-3 py-2 w-14 text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Collection</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Calibre</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Case Size</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Movement</th>
              </tr>
            </thead>
            <tbody>
              {filteredReferences.map((ref) => (
                <ReferenceRow
                  key={ref.id}
                  reference={ref}
                  onClick={() => onSelectReference(ref)}
                  selected={selectedIds.has(ref.id)}
                  onSelect={() => toggleSelect(ref.id)}
                  onEnrichModule={() => handleEnrichModule(ref)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty states */}
      {algoliaMode && hits.length === 0 && !loadingHits && (
        <div className="text-center py-12 text-gray-500">
          {isSearchMode || hasFacetFilter
            ? `No references ${[isSearchMode ? `matching "${activeQuery}"` : null, filterLabel || null].filter(Boolean).join(' · ')}`
            : 'No references found for this brand'}
        </div>
      )}
      {/* Loaded refs but nothing visible. With no filter applied that means the
          loaded page(s) hold only Shopify variants, which are managed from their
          parent's editor — paging on will reach the next parent. */}
      {/* `browseBase`, not `references`: when a filter is browsing the full
          in-memory brand set the loaded browse page is not what's on screen, and
          with the "without calibre" toggle on it was never fetched at all — so
          reading `references` here would claim an empty brand over a populated
          grid. */}
      {!algoliaMode && !loadingFullBrandSet && filteredReferences.length === 0 && browseBase.length > 0 && (
        <div className="text-center py-8 text-gray-500">
          {hasFacetFilter
            ? 'No references match the current filter'
            : 'No parent references on the loaded pages — every ref loaded so far is a Shopify variant. Load more to reach the next parent.'}
        </div>
      )}
      {!algoliaMode && !loadingFullBrandSet && browseBase.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          No references found for this brand
        </div>
      )}

      {/* Load More — the listing is paged out of Algolia. */}
      {algoliaMode && !loadingHits && hits.length < hitsReachable && (
        <div className="flex justify-center pt-4">
          <Button
            variant="secondary"
            onClick={loadMoreHits}
            loading={loadingMoreHits}
          >
            Load More ({hits.length} of {hitsTotal.toLocaleString()})
          </Button>
        </div>
      )}

      {/* Say it out loud when the index won't hand back the whole set, rather
          than just running out of Load More. */}
      {algoliaMode &&
        !loadingHits &&
        hitsTotal > ALGOLIA_MAX_RETRIEVABLE &&
        hits.length >= hitsReachable && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
            Showing the first {ALGOLIA_MAX_RETRIEVABLE.toLocaleString()} of{' '}
            {hitsTotal.toLocaleString()} matches — search can't page past that.
            Narrow the search or filters to reach the rest.
          </div>
        )}

      {/* Load More (Firestore listing) — hidden when a filter is active on the
          fallback path, where the full brand set is already in memory. */}
      {!algoliaMode && !browsingFullBrandSet && hasMore && (
        <div className="flex justify-center pt-4">
          <Button variant="secondary" onClick={loadMoreReferences} loading={loadingMore}>
            Load More ({loadedParentCount} of {parentCount})
          </Button>
        </div>
      )}

      {/* ── Select all brand banner ──────────────────────────────────────────────
          Hidden whenever a search or filter is active: it offers a BRAND-WIDE
          selection, which in a narrowed view would silently reach far past
          what's on screen. */}
      {!isSearchMode && !hasFacetFilter && allFilteredSelected && !selectAllBrand && parentCount > visibleIds.length && (
        <div className="text-center py-2 bg-blue-50 border border-blue-200 rounded-lg mx-4 mb-2">
          <span className="text-sm text-blue-700">
            All {visibleIds.length} visible references selected.{' '}
            <button
              onClick={handleSelectAllBrand}
              className="font-semibold underline hover:text-blue-900"
            >
              Select all {parentCount.toLocaleString()} references for {brand.displayName || brand.name}
            </button>
          </span>
        </div>
      )}
      {selectAllBrand && (
        <div className="text-center py-2 bg-blue-50 border border-blue-200 rounded-lg mx-4 mb-2">
          <span className="text-sm text-blue-700 font-medium">
            All {selectedIds.size.toLocaleString()} references for {brand.displayName || brand.name} are selected.
          </span>
        </div>
      )}

      {/* ── Bulk actions over the current selection ─────────────────────────── */}
      {selectedIds.size > 0 && (
        <ReferenceBulkActionBar
          brand={brand}
          selectedRefs={effectiveSelectedRefs}
          loadingSelection={loadingSearchRefs}
          selectionLabelSuffix={selectAllBrand ? ' (all)' : ''}
          onClearSelection={clearSelection}
          onRefsUpdated={(ids, updates) => {
            setReferences(prev =>
              prev.map(r =>
                ids.has(r.id)
                  ? applyDotPathUpdates(r as unknown as Record<string, unknown>, updates) as unknown as WatchReference
                  : r,
              ),
            );
            // Apply the same updates to the rendered hits so an edited row
            // doesn't keep showing its pre-edit calibre/collection until
            // syncReferenceToAlgolia catches up. Dot-path updates that address
            // nested objects simply don't match a flat hit and are no-ops.
            setHits(prev =>
              prev.map(h =>
                ids.has(h.referenceId)
                  ? applyDotPathUpdates(h as unknown as Record<string, unknown>, updates) as unknown as WatchReferenceAlgoliaHit
                  : h,
              ),
            );
          }}
          onRefsDeleted={(ids) => {
            setReferences(prev => prev.filter(r => !ids.has(r.id)));
            setHits(prev => prev.filter(h => !ids.has(h.referenceId)));
            setHitsTotal(prev => Math.max(0, prev - ids.size));
            setTotalCount(prev => Math.max(0, prev - ids.size));
            // Only parents/standalones are selectable from this listing, so every
            // deleted id came off the parent count. Adjusted optimistically —
            // the Algolia count behind it lags the delete triggers.
            setParentCount(prev => Math.max(0, prev - ids.size));
            setSelectedIds(new Set());
          }}
          onRefsEnriched={refreshAfterEnrich}
        />
      )}

      {/* ── Delete ALL refs for brand modal ─────────────────────────────────── */}
      <Modal
        isOpen={showDeleteAllModal}
        onClose={() => { if (!isDeletingAll) { setShowDeleteAllModal(false); setDeleteAllConfirmText(''); } }}
        title="Delete all references"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-semibold text-red-800">
                This will delete ALL references for {brandLabel} ({totalCount.toLocaleString()} ref{totalCount !== 1 ? 's' : ''} total). Cannot be undone.
              </p>
              <p className="text-sm text-red-700 mt-1">
                Every reference under this brand — parents, Shopify variants, and standalones — will be permanently removed. Use this only to wipe the brand before re-importing.
              </p>
            </div>
          </div>

          {isDeletingAll ? (
            <div className="space-y-3 py-2">
              <p className="text-sm text-gray-600">
                Deleting {deleteAllProgress.toLocaleString()} of {deleteAllTotal.toLocaleString()}...
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-600 h-2 rounded-full transition-all"
                  style={{ width: `${deleteAllTotal > 0 ? (deleteAllProgress / deleteAllTotal) * 100 : 0}%` }}
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm text-gray-700 mb-1.5">
                Type <span className="font-semibold text-gray-900">{brandLabel}</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                placeholder={brandLabel}
                autoFocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => { setShowDeleteAllModal(false); setDeleteAllConfirmText(''); }}
              disabled={isDeletingAll}
            >
              Cancel
            </Button>
            <button
              onClick={handleDeleteAll}
              disabled={isDeletingAll || deleteAllConfirmText.trim() !== brandLabel || totalCount === 0}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              {isDeletingAll && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              {isDeletingAll
                ? `Deleting ${deleteAllProgress.toLocaleString()}/${deleteAllTotal.toLocaleString()}`
                : `Delete all ${totalCount.toLocaleString()} ref${totalCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Create reference modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={isCreating}
        onClose={resetCreateForm}
        title={`Add Reference to ${brand.name}`}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newReference}
              onChange={(e) => setNewReference(e.target.value)}
              placeholder="e.g., 116610LN"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            {newReference.length > 0 && !normalizeReference(newReference) ? (
              <p className="text-xs text-red-500 mt-1">
                Reference must contain at least one alphanumeric character
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                This will be used to generate the reference ID
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            <input
              type="text"
              value={newModel}
              onChange={(e) => setNewModel(e.target.value)}
              placeholder="e.g., Submariner Date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Collection</label>
            <input
              type="text"
              value={newCollection}
              onChange={(e) => setNewCollection(e.target.value)}
              placeholder="e.g., Professional"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
            <input
              type="text"
              value={newProductName}
              onChange={(e) => setNewProductName(e.target.value)}
              placeholder="e.g., Oyster Perpetual Submariner Date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
            You can add more details after creating the reference by clicking on it to open the full editor.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={resetCreateForm} disabled={isProcessing}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              loading={isProcessing}
              disabled={!normalizeReference(newReference)}
            >
              Create Reference
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Module enrichment (per-row): edit the linked module + AI enrich ──── */}
      <ElectronicModuleEditorModal
        isOpen={!!enrichModuleTarget}
        onClose={() => setEnrichModuleTarget(null)}
        module={enrichModuleTarget}
        onSaved={() => setEnrichModuleTarget(null)}
        onDeleted={() => setEnrichModuleTarget(null)}
      />

      {showRunScraper && (
        <RunScraperModal
          isOpen={showRunScraper}
          onClose={() => setShowRunScraper(false)}
          brand={brand}
          onOpenJobs={onOpenExtractorJobs}
          onImported={loadReferences}
        />
      )}
    </div>
  );
}
